import { useState } from "react";
import { Link, router } from "@inertiajs/react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ArrowRight, Coins, Layers, Plus, PlusCircle, Trash2 } from "lucide-react";
import { SCORE_LABELS } from "@/constants/forms";

interface AverageScores {
  [key: string]: number | null | undefined;
}

interface Submission {
  id: string;
  photoUrl: string | null;
  category: string;
  votesTarget: number;
  votesCount: number;
  status: string;
  paid: boolean;
  completedAt: string | null;
  averageScores?: AverageScores | null;
}

interface Props {
  submissions: Submission[];
  paidCredits: number;
  user: {
    email: string;
  };
}

export default function Index({ submissions, paidCredits, user }: Props) {
  return (
    <Layout title="Minhas Fotos" user={user}>
      <div className="mx-auto max-w-6xl px-4 py-10">
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-4xl">
              Minhas Fotos
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Acompanhe os resultados das suas fotos
            </p>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline" className="gap-2">
              <Link href="/votes">
                <ArrowRight size={16} /> Votar
              </Link>
            </Button>
            <Button asChild className="gap-2">
              <Link href="/submissions/new">
                <Plus size={16} /> Nova Foto
              </Link>
            </Button>
          </div>
        </header>

        {submissions.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {submissions.map((submission) => (
              <SubmissionCard
                key={submission.id}
                submission={submission}
                paidCredits={paidCredits}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

interface SubmissionCardProps {
  submission: Submission;
  paidCredits: number;
}

const VOTE_OPTIONS = [10, 20, 40] as const;

function SubmissionCard({ submission, paidCredits }: SubmissionCardProps) {
  const [addVotesOpen, setAddVotesOpen] = useState(false);
  const [confirmVotes, setConfirmVotes] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const canAddVotes = submission.status === "active" || submission.status === "completed";

  function handleAddVotes(amount: number) {
    setProcessing(true);
    router.patch(
      `/submissions/${submission.id}/add_votes`,
      { votes_to_add: amount },
      {
        onFinish: () => {
          setProcessing(false);
          setConfirmVotes(null);
          setAddVotesOpen(false);
        },
      }
    );
  }

  function handleDelete() {
    setProcessing(true);
    router.delete(`/submissions/${submission.id}`, {
      onFinish: () => {
        setProcessing(false);
        setDeleteOpen(false);
      },
    });
  }

  return (
    <Card className="border-border bg-card">
      <div className="relative flex items-center justify-center bg-background">
        {submission.photoUrl ? (
          <img src={submission.photoUrl} alt="Foto" className="w-full" />
        ) : (
          <div className="flex aspect-square w-full items-center justify-center text-muted-foreground">
            Sem imagem
          </div>
        )}
        <span
          className={cn(
            "absolute right-3 top-3 rounded-full px-3 py-1 text-xs font-semibold capitalize",
            statusColor(submission.status)
          )}
        >
          {statusLabel(submission.status)}
        </span>
      </div>

      <CardHeader className="pb-2">
        <CardDescription>
          {submission.completedAt
            ? `Concluida em ${formatDate(submission.completedAt)}`
            : "Coletando votos"}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm text-foreground">
            <span>Progresso</span>
            <span className="font-semibold">
              {submission.votesCount} / {submission.votesTarget}
            </span>
          </div>
          <Progress value={progressPercent(submission)} className="mt-2" />
        </div>

        {submission.averageScores && (
          <div className="rounded-lg border border-border bg-muted p-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Layers size={14} /> Notas
            </div>
            <div className="mt-3 grid grid-cols-1 min-[360px]:grid-cols-2 gap-2 text-sm">
              {Object.entries(submission.averageScores).map(([key, value]) => (
                <div
                  key={key}
                  className={cn(
                    "flex items-center justify-between rounded-md border px-3 py-2",
                    scoreTone(value ?? 0)
                  )}
                >
                  <span className="text-foreground">{SCORE_LABELS[key] ?? key}</span>
                  <span className="font-semibold">{value?.toFixed(1) ?? "–"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          {canAddVotes && (
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-1.5"
              onClick={() => setAddVotesOpen(true)}
            >
              <PlusCircle size={14} /> Adicionar Votos
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </CardContent>

      {/* Add Votes Dialog */}
      <Dialog open={addVotesOpen} onOpenChange={(open) => { setAddVotesOpen(open); if (!open) setConfirmVotes(null); }}>
        <DialogContent>
          {confirmVotes === null ? (
            <>
              <DialogHeader>
                <DialogTitle>Adicionar Votos</DialogTitle>
                <DialogDescription>
                  Escolha quantos votos deseja adicionar. Cada voto custa 1 crédito pago.
                  {submission.status === "completed" && (
                    <span className="mt-1 block text-amber-400">
                      A foto será reativada para receber novos votos.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">
                  Seus créditos pagos: <span className="font-semibold text-foreground">{paidCredits}</span>
                </p>
                {VOTE_OPTIONS.map((amount) => (
                  <Button
                    key={amount}
                    variant="outline"
                    className="justify-between"
                    disabled={paidCredits < amount}
                    onClick={() => setConfirmVotes(amount)}
                  >
                    <span>+{amount} votos</span>
                    <span className="text-muted-foreground">{amount} créditos</span>
                  </Button>
                ))}
              </div>
              <DialogFooter className="flex-row gap-2 sm:justify-between">
                <Button asChild variant="outline" className="gap-1.5">
                  <Link href="/paid_credits">
                    <Coins size={14} /> Adicionar Créditos
                  </Link>
                </Button>
                <Button variant="ghost" onClick={() => setAddVotesOpen(false)}>
                  Cancelar
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Confirmar</DialogTitle>
                <DialogDescription>
                  Deseja adicionar <span className="font-semibold text-foreground">{confirmVotes} votos</span> usando{" "}
                  <span className="font-semibold text-foreground">{confirmVotes} créditos pagos</span>?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setConfirmVotes(null)}>
                  Voltar
                </Button>
                <Button disabled={processing} onClick={() => handleAddVotes(confirmVotes)}>
                  Confirmar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir foto</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta foto? Ela não aparecerá mais na sua lista.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={processing}
              onClick={handleDelete}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card className="mt-10 border-border bg-card text-center">
      <CardHeader>
        <CardTitle className="text-2xl">Nenhuma foto ainda</CardTitle>
        <CardDescription>
          Envie sua primeira foto para começar a receber avaliações.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 pb-8">
        <Button asChild className="gap-2">
          <Link href="/submissions/new">
            <Plus size={16} /> Nova Foto
          </Link>
        </Button>
        <Button asChild variant="ghost" className="gap-2 text-muted-foreground">
          <Link href="/votes">
            <ArrowRight size={16} /> Votar enquanto espera
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function progressPercent(sub: Submission) {
  return Math.min((sub.votesCount / sub.votesTarget) * 100, 100);
}

function statusLabel(status: string) {
  switch (status) {
    case "active":
      return "Ativa";
    case "completed":
      return "Concluída";
    case "created":
    case "pending":
      return "Pendente";
    default:
      return status;
  }
}

function statusColor(status: string) {
  switch (status) {
    case "active":
      return "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-500/30";
    case "completed":
      return "bg-blue-500/15 text-blue-200 ring-1 ring-blue-500/30";
    case "created":
    case "pending":
      return "bg-amber-500/15 text-amber-200 ring-1 ring-amber-500/30";
    default:
      return "bg-slate-600/40 text-slate-200";
  }
}

function scoreTone(score: number) {
  if (score >= 4.2) return "bg-emerald-500/10 border-emerald-500/30";
  if (score >= 3.4) return "bg-blue-500/10 border-blue-500/30";
  if (score >= 2.6) return "bg-amber-500/10 border-amber-500/30";
  return "bg-rose-500/10 border-rose-500/30";
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("pt-BR", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
